// AF-3.19 P0 — AI Discovery Command Center. The unified, first-class "How
// is AI discovering this brand?" surface the product's own thesis
// requires (spec: "AI Visibility should become a first-class product
// concept. Do not hide it inside Growth.").
//
// CRITICAL: every value here traces to a real, existing backend route.
// This page performs NO client-side scoring or synthesis of new numbers
// -- it renders exactly what /som/dashboard, /v2/intelligence/{id}/overview,
// /v2/intelligence/{id}/competitors, /v2/intelligence/{id}/sources, and
// /v2/intelligence/{id}/opportunities return, verbatim. Anything those
// routes do not provide is rendered as an explicit NOT_AVAILABLE /
// UNKNOWN state (see UnavailableKpi below) -- never a fabricated number.
// This directly closes the AF-3.19 audit's "no unified AI Visibility
// surface" and "citation tracking is missing" findings by being honest
// about the second one rather than inventing citation data.
//
// AF-3.20.5 -- light-theme visual pass. Page organized around the six
// questions the spec frames as "how AI sees your brand": visibility, AI
// answers, competitive context, sources/citations, changes, growth
// implications. No data source, API call, or taxonomy string changed.
//
// P2.2 -- multi-client audit: this page had its own ad-hoc clientsAPI.list()
// + <Select> client picker instead of the shared useWorkspaceSelection()/
// WorkspaceSelect mechanism every other multi-client Growth surface uses
// (Signals/Diagnoses/Recommendations/Action Plans/Company Context -- see
// their own P1.7/P1.8 history). Functionally switchable either way, but
// two real defects came with the duplication: (1) switching clients left
// the PREVIOUS client's som/overview/competitors/sources/opportunities on
// screen until the new fetch resolved -- every other page in this sweep
// clears that state first; (2) it never surfaced company_context.company_name,
// so the picker always showed the raw domain even for a brand the founder
// had a real discovered identity for. Swapping to the shared hook/component
// fixes both by construction and matches the rest of the product.
import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { ConfidenceIndicator } from '@/components/brand/Badges';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { GLIMState } from '@/brand';
import { WorkspaceSelect } from '@/components/brand/BrandContextBar';
import { Metric } from '@/components/brand/Metric';
import { useWorkspaceSelection } from '@/hooks/useWorkspaceSelection';
import { somDashboardAPI, intelligenceAPI } from '@/lib/api';
import { Binoculars, ChatCircleText, Users, Link as LinkIcon, Target } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

function SectionHeading({ icon: Icon, children }) {
  return (
    <h2 className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-2">
      {Icon && <Icon size={13} className="text-[var(--text-tertiary)]" />}
      {children}
    </h2>
  );
}

// Day 2.5 -- kept distinct from LiveKpi/<Metric> on purpose: this renders
// the specific "NOT_AVAILABLE" truth-state string plus its own
// explanation, which is NOT the same claim as <Metric>'s generic
// "UNKNOWN" (a null backend value) -- collapsing the two into one
// component would silently lose that distinction. Visual-only update:
// tone="inset" (restrained, carved-in) rather than the raised/flat card
// a real value gets, so an unavailable metric never reads as more
// "finished" than it is.
function UnavailableKpi({ label }) {
  return (
    <div className="rounded-[var(--radius-card-soft)] border border-[var(--border-light)] bg-[var(--surface-inset)] shadow-[var(--shadow-inset)] p-4" data-testid={`ai-discovery-kpi-unavailable-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className="text-[var(--text-2xs)] font-mono uppercase text-[var(--text-tertiary)] mb-2">{label}</p>
      <p className="text-[var(--text-base)] text-[var(--text-tertiary)] italic">NOT_AVAILABLE</p>
      <p className="text-[var(--text-2xs)] text-[var(--text-tertiary)] mt-1">No real backend source exists for this metric yet.</p>
    </div>
  );
}

// Day 2.5 -- replaced this page's own local KPI card markup with the
// shared <Metric> (components/brand/Metric.jsx). Same null/undefined ->
// "UNKNOWN" rule, same StatusBadge underneath -- behavior unchanged,
// only the surface/shadow/radius/type-scale tokens are new.
function LiveKpi({ label, value, statusValue, sublabel }) {
  return <Metric label={label} value={value} status={statusValue} sublabel={sublabel} testId={`ai-discovery-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`} />;
}

export default function AIDiscoveryCommandCenterPage() {
  const { clients, workspaceId, setWorkspaceId, loadingClients } = useWorkspaceSelection();
  const clientId = workspaceId || null;
  const [som, setSom] = useState(null);
  const [overview, setOverview] = useState(null);
  const [competitors, setCompetitors] = useState(null);
  const [sources, setSources] = useState(null);
  const [opportunities, setOpportunities] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    // Clear the previous client's data before fetching the new one --
    // otherwise it stays on screen (looking like the new client's own
    // numbers) until this fetch resolves.
    setSom(null); setOverview(null); setCompetitors(null); setSources(null); setOpportunities(null);
    // P2.3 -- scope Share of Model to the selected brand, not the newest
    // som_scores row account-wide (which would show another brand's score).
    const calls = [somDashboardAPI.get(clientId).then((r) => r.data).catch(() => null)];
    if (clientId) {
      calls.push(
        intelligenceAPI.overview(clientId).then((r) => r.data).catch(() => null),
        intelligenceAPI.competitors(clientId).then((r) => r.data).catch(() => null),
        intelligenceAPI.sources(clientId).then((r) => r.data).catch(() => null),
        intelligenceAPI.opportunities(clientId).then((r) => r.data).catch(() => null),
      );
    }
    Promise.all(calls)
      .then(([somData, overviewData, competitorsData, sourcesData, opportunitiesData]) => {
        setSom(somData);
        setOverview(overviewData || null);
        setCompetitors(competitorsData || null);
        setSources(sourcesData || null);
        setOpportunities(opportunitiesData || null);
      })
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [clientId]);

  useEffect(() => { if (!loadingClients) load(); }, [loadingClients, load]);

  const somKpi = som?.kpi_cards?.som;
  const opportunityList = Array.isArray(opportunities) ? opportunities : (opportunities?.opportunities || []);
  const competitorList = competitors?.competitors || overview?.competitors || [];
  const sourceList = Array.isArray(sources) ? sources : (sources?.sources || sources?.domains || []);

  return (
    <DashboardLayout>
      <div data-testid="ai-discovery-command-center-page" className="max-w-5xl mx-auto">
        <div className="mb-5 flex items-center gap-3">
          {/* GLIM = observation: the face of what Martian is watching here. */}
          <GLIMState state={error ? 'error' : loading ? 'discovering' : 'neutral'} size={52} showLabel={false} className="shrink-0" />
          <div>
            <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">How AI sees this brand</h1>
            <p className="text-[12px] text-[var(--text-secondary)]">How AI, search, and social currently discover, mention, and recommend this brand — real evidence only.</p>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-5" data-testid="ai-discovery-client-bar">
          {clients.length > 0 && (
            <WorkspaceSelect clients={clients} workspaceId={workspaceId} setWorkspaceId={setWorkspaceId} />
          )}
        </div>

        {loading && <LoadingState />}
        {error && <ErrorState error={error} onRetry={load} />}

        {!loading && !error && !loadingClients && clients.length === 0 && (
          <EmptyState
            title="Connect a brand"
            description="No brand workspace exists yet."
            whyItMatters="AI visibility signals only render once Martian has a real brand entity to measure — connect a data source to begin measuring this signal."
            actionLabel="Add a brand"
          />
        )}

        {!loading && !error && clients.length > 0 && (
          <>
            {/* 1. Visibility */}
            <section className="mb-6" aria-labelledby="ai-discovery-visibility-heading">
              <SectionHeading icon={Binoculars}>
                <span id="ai-discovery-visibility-heading">How visible is this brand to AI?</span>
              </SectionHeading>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <LiveKpi
                  label="Share of Model"
                  value={somKpi?.is_live ? somKpi.value : null}
                  sublabel={somKpi?.is_live ? `Source: ${somKpi.source}` : 'No SoM measurement recorded yet'}
                  statusValue={somKpi?.status ? somKpi.status.toUpperCase() : 'UNKNOWN'}
                />
                <LiveKpi
                  label="Search Everywhere Score"
                  value={overview?.search_everywhere_score?.score ?? overview?.search_everywhere_score ?? null}
                  sublabel={clientId ? undefined : 'Select a brand to load'}
                />
                <UnavailableKpi label="AI Traffic (referral)" />
                <UnavailableKpi label="Discovery Reach" />
                <UnavailableKpi label="AI Generated Visibility Rate" />
              </div>
            </section>

            {/* 2. AI Answers */}
            <section className="mb-6" aria-labelledby="ai-discovery-environments-heading">
              <SectionHeading icon={ChatCircleText}>
                <span id="ai-discovery-environments-heading">Which AI/search environments and queries matter</span>
              </SectionHeading>
              <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]">
                {overview?.total_executions ? (
                  <p className="text-[12px] text-[var(--text-primary)]">{overview.total_executions} monitored prompt executions recorded for this brand.</p>
                ) : (
                  <p className="text-[12px] text-[var(--text-tertiary)] italic" data-testid="ai-discovery-prompts-unknown">UNKNOWN — no execution count returned by the backend for this brand yet.</p>
                )}
                <Link to="/search-everywhere" className="text-[11px] text-[var(--accent)] font-medium mt-2 inline-block">View full prompt/query detail →</Link>
              </div>
            </section>

            {/* 3. Competitive context */}
            <section className="mb-6" aria-labelledby="ai-discovery-presence-heading">
              <SectionHeading icon={Users}>
                <span id="ai-discovery-presence-heading">Where the brand appears — and where competitors appear instead</span>
              </SectionHeading>
              <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]">
                {competitorList.length === 0 ? (
                  <p className="text-[12px] text-[var(--text-tertiary)] italic" data-testid="ai-discovery-competitors-empty">INSUFFICIENT_EVIDENCE — no competitor comparison available for this brand yet.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {competitorList.slice(0, 8).map((c, i) => (
                      <li key={i} className="flex items-center justify-between text-[11px] text-[var(--text-secondary)]" data-testid="ai-discovery-competitor-row">
                        <span className="text-[var(--text-primary)]">{c.name || c.competitor_name || c.domain || `Competitor ${i + 1}`}</span>
                        <span className="font-mono">{c.visibility_rate !== undefined ? `${c.visibility_rate}%` : (c.mentions !== undefined ? `${c.mentions} mentions` : '—')}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            {/* 4. Sources / citations */}
            <section className="mb-6" aria-labelledby="ai-discovery-sources-heading">
              <SectionHeading icon={LinkIcon}>
                <span id="ai-discovery-sources-heading">Source domains cited alongside this brand</span>
              </SectionHeading>
              <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]">
                {sourceList.length === 0 ? (
                  <p className="text-[12px] text-[var(--text-tertiary)] italic" data-testid="ai-discovery-sources-empty">NOT_AVAILABLE — Martian does not yet track per-citation source detail; only aggregate source-domain evidence is shown when present.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {sourceList.slice(0, 20).map((s, i) => (
                      <span key={i} className="text-[10px] font-mono text-[var(--text-secondary)] bg-[var(--surface-sunken)] border border-[var(--border)] rounded px-1.5 py-0.5" data-testid="ai-discovery-source-chip">
                        {typeof s === 'string' ? s : (s.domain || s.source || JSON.stringify(s))}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* 5/6. Important changes -> Growth implications */}
            <section aria-labelledby="ai-discovery-opportunities-heading">
              <SectionHeading icon={Target}>
                <span id="ai-discovery-opportunities-heading">Opportunities emerging from this evidence</span>
              </SectionHeading>
              <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]">
                {opportunityList.length === 0 ? (
                  <p className="text-[12px] text-[var(--text-tertiary)] italic" data-testid="ai-discovery-opportunities-empty">No AI-discovery-specific opportunities detected yet.</p>
                ) : (
                  <ul className="space-y-1.5 mb-2">
                    {opportunityList.slice(0, 5).map((o, i) => (
                      <li key={i} className="text-[11px] text-[var(--text-primary)]" data-testid="ai-discovery-opportunity-row">
                        {o.title || o.description || o.type || 'Opportunity'}
                      </li>
                    ))}
                  </ul>
                )}
                <Link to="/growth/opportunities" className="text-[11px] text-[var(--accent)] font-medium inline-block">View in Growth Opportunity Portfolio →</Link>
              </div>
            </section>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
